/**
 * The migration detail's cohort panels (AD-1d): pure selectors for the card-settlement inventory,
 * the approval batch, the card-keys waiver and the earn drain.
 *
 * Mirrors elios-backend wallet-service 0.76.0 to 0.78.0: `entities/wallet-migration.yaml`
 * (`WalletMigrationSnapshot.cardSettlement`, `leftBehind`, `approvalSessionKeys`,
 * `cardKeysWaiver`), `card-settlement/` (the quiesce, the inventory, the rekey and what it leaves on
 * the source on purpose), `wallet-migration/redrive.service.ts` (`waiveCardKeys`), and earn's
 * `wallet-migration-drain.yaml`. The backend re-checks everything; these only decide what to show.
 */
import type { MantineColor } from "@mantine/core";
import type {
  HoldDoc,
  SessionKeyDoc,
  SettlementDoc,
  SettlementObligationDoc,
  WalletMigrationDoc,
  WalletMigrationDrainDoc,
  WalletMigrationDrainVault,
  WithDocId,
} from "@/firestore/types";
import { formatTimestamp } from "@/lib/datetime";
import type { AlertMarker, CommandAvailability } from "./migrations";

function ms(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

// ───────────────────────────── start refusals ─────────────────────────────

/** `WalletMigrationRequestRefusal`: why the owner's `POST /walletMigrations` was refused. */
export const MIGRATION_START_REFUSAL_LABELS: Readonly<Record<string, string>> = {
  reactivatedNeedsSupport: "Reactivated wallet: needs support",
  otherMigrationActive: "Another wallet of the owner is migrating",
  previousMigrationIncomplete: "A previous migration did not complete",
  abandonedSuccessorNeedsSupport: "An abandoned successor cannot be reused",
  notCurrentWallet: "Not the owner's current wallet",
  notEligible: "Wallet not provisioned",
  alreadyCurrent: "Already on the target generation",
  planMissing: "No migration plan to the target",
  ownerRotationUnsupported: "Owner-key rotation is not supported",
  targetUnavailable: "Target contracts not proven deployed (transient)",
  upgradeNotOffered: "No live upgrade offer",
  // Earn cohort (wallet-service 0.76.0): limUSD redemptions take days, so it exits first.
  vaultExitPending: "limUSD still to exit",
  // Card cohort (0.77.0): the snapshot's quiesce would wait on it forever.
  operationNeedsReview: "An operation needs support first",
};

/** What support does when the owner reports one of the cohort refusals. */
export const MIGRATION_START_REFUSAL_HELP: Readonly<Record<string, string>> = {
  vaultExitPending:
    "The wallet still has limUSD to exit (shares worth redeeming, a withdrawal admitted but not landed, or a redemption still to be paid). The owner withdraws limUSD first; once earn shows it paid out, the start succeeds. Dust below the vault's minimum redemption does not block.",
  operationNeedsReview:
    "An operation of the wallet needs support first: a quarantined (submissionUnknown) smart-account operation, a dead or quarantined off-ramp burn, or a card refund release that exhausted its attempts. Resolve it (the quarantine resolve, the burn reaper, the settlement obligation), then the owner starts again.",
};

export function migrationStartRefusalLabel(code: string | null | undefined): string | undefined {
  return code ? MIGRATION_START_REFUSAL_LABELS[code] : undefined;
}

// ───────────────────────────── the Spanner row (GET /walletMigrations/{id}) ─────────────────────────────

export interface CardSettlementObligationEntry {
  id: string;
  state: string;
  /** `amountInReserve − amountSettled − amountReleased`; never asserted non-negative. */
  positionMicroUsdc: number;
}

/** `WalletMigrationCardSettlementInventory`: the source's card settlement when the quiesce let go. */
export interface CardSettlementInventory {
  openObligations: CardSettlementObligationEntry[];
  liveReservePositionMicroUsdc: number;
  scheduledLegs: number;
  liveCardAuthHoldsMicroUsdc: number;
  postBurnSettlements: string[];
  exhaustedItems: string[];
  legacyNegativeObligations: string[];
  onChainReserveMicroUsdc: number;
  reserveMovedByIncludedLegMicroUsdc: number;
  reserveShortfallMicroUsdc: number;
}

export interface LeftBehindAsset {
  asset: string;
  token: string | null;
  address: string;
  /** Base units (wei for HYPE), a decimal string. */
  amount: string;
}

export interface MigrationSnapshot {
  oldMainBalanceMicroUsdc: number | null;
  oldReserveBalanceMicroUsdc: number | null;
  blockNumber: number | null;
  readAt: string | null;
  /** `undefined`: taken before wallet-service 0.77.0 (no inventory recorded). */
  cardSettlement: CardSettlementInventory | null | undefined;
  leftBehind: LeftBehindAsset[];
}

export interface CardKeysWaiver {
  by: string;
  reason: string;
  at: string;
}

/** What the console reads from the Spanner row that Firestore does not project. */
export interface MigrationRowExtras {
  snapshot: MigrationSnapshot | null;
  cardKeysWaiver: CardKeysWaiver | null;
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** An integer from a JSON number or a decimal string (Spanner INT64 may serialise either way). */
function int(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^-?\d+$/.test(value.trim())) return Number(value);
  return null;
}

function str(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function readInventory(value: unknown): CardSettlementInventory | null {
  const r = record(value);
  if (!r) return null;
  const obligations = Array.isArray(r.openObligations) ? r.openObligations : [];
  return {
    openObligations: obligations.flatMap((o) => {
      const e = record(o);
      const id = str(e?.id);
      return e && id
        ? [
            {
              id,
              state: str(e.state) ?? "unknown",
              positionMicroUsdc: int(e.positionMicroUsdc) ?? 0,
            },
          ]
        : [];
    }),
    liveReservePositionMicroUsdc: int(r.liveReservePositionMicroUsdc) ?? 0,
    scheduledLegs: int(r.scheduledLegs) ?? 0,
    liveCardAuthHoldsMicroUsdc: int(r.liveCardAuthHoldsMicroUsdc) ?? 0,
    postBurnSettlements: strings(r.postBurnSettlements),
    exhaustedItems: strings(r.exhaustedItems),
    legacyNegativeObligations: strings(r.legacyNegativeObligations),
    onChainReserveMicroUsdc: int(r.onChainReserveMicroUsdc) ?? 0,
    reserveMovedByIncludedLegMicroUsdc: int(r.reserveMovedByIncludedLegMicroUsdc) ?? 0,
    reserveShortfallMicroUsdc: int(r.reserveShortfallMicroUsdc) ?? 0,
  };
}

/**
 * Reads `snapshot` and `cardKeysWaiver` from the `GET /walletMigrations/{id}` body (the Spanner
 * entity, returned whole to an admin). Defensive: an unexpected shape reads as absent, never throws.
 */
export function readMigrationRow(body: unknown): MigrationRowExtras {
  const row = record(body);
  const s = record(row?.snapshot);
  const w = record(row?.cardKeysWaiver);
  const snapshot: MigrationSnapshot | null = s
    ? {
        oldMainBalanceMicroUsdc: int(s.oldMainBalanceMicroUsdc),
        oldReserveBalanceMicroUsdc: int(s.oldReserveBalanceMicroUsdc),
        blockNumber: int(s.blockNumber),
        readAt: str(s.readAt),
        cardSettlement:
          s.cardSettlement === undefined ? undefined : readInventory(s.cardSettlement),
        leftBehind: (Array.isArray(s.leftBehind) ? s.leftBehind : []).flatMap((a) => {
          const e = record(a);
          const asset = str(e?.asset);
          return e && asset
            ? [
                {
                  asset,
                  token: str(e.token),
                  address: str(e.address) ?? "",
                  amount: str(e.amount) ?? String(e.amount ?? ""),
                },
              ]
            : [];
        }),
      }
    : null;
  const cardKeysWaiver =
    w && str(w.by) && str(w.at)
      ? { by: str(w.by)!, reason: str(w.reason) ?? "", at: str(w.at)! }
      : null;
  return { snapshot, cardKeysWaiver };
}

// ───────────────────────────── card settlement ─────────────────────────────

/** Obligation states the card cohort counts as open (`card-settlement/cohort.ts`). */
export const OPEN_OBLIGATION_STATES = ["accruing", "captured", "refundPending"] as const;

export function isOpenObligation(o: Pick<SettlementObligationDoc, "state">): boolean {
  return (OPEN_OBLIGATION_STATES as readonly string[]).includes(o.state);
}

/** An obligation's live Reserve position: `amountInReserve − amountSettled − amountReleased`. */
export function obligationPositionMicroUsdc(
  o: Pick<SettlementObligationDoc, "amountInReserveMicroUsdc"> &
    Partial<Pick<SettlementObligationDoc, "amountSettledMicroUsdc" | "amountReleasedMicroUsdc">>,
): number {
  return (
    (o.amountInReserveMicroUsdc ?? 0) -
    (o.amountSettledMicroUsdc ?? 0) -
    (o.amountReleasedMicroUsdc ?? 0)
  );
}

/** CCTP settlement states past which nothing more happens. */
export const TERMINAL_SETTLEMENT_STATES = ["settled", "failed"] as const;

/** Settlements short of terminal (a post-burn CCTP settlement stays on its old wallet on purpose). */
export function liveSettlements<T extends Pick<SettlementDoc, "state">>(rows: readonly T[]): T[] {
  return rows.filter((s) => !(TERMINAL_SETTLEMENT_STATES as readonly string[]).includes(s.state));
}

export function liveHolds<T extends Pick<HoldDoc, "state">>(rows: readonly T[]): T[] {
  return rows.filter((h) => h.state === "pending" || h.state === "active");
}

export type ObligationLocation = "successor" | "source" | "elsewhere" | "notLoaded";

/**
 * Where each obligation of the snapshot's inventory is now, by its `walletId`: on the successor once
 * the rekey moved it, still on the source before (or when the rekey missed it).
 */
export function inventoryObligationLocations(input: {
  inventory: CardSettlementInventory;
  sourceWallet: string;
  successorWallet: string | null;
  obligations: readonly Pick<WithDocId<SettlementObligationDoc>, "_docId" | "walletId" | "state">[];
}): Map<string, { location: ObligationLocation; state: string | null }> {
  const byId = new Map(input.obligations.map((o) => [o._docId, o]));
  const out = new Map<string, { location: ObligationLocation; state: string | null }>();
  for (const entry of input.inventory.openObligations) {
    const now = byId.get(entry.id);
    out.set(entry.id, {
      location: !now
        ? "notLoaded"
        : now.walletId === input.successorWallet
          ? "successor"
          : now.walletId === input.sourceWallet
            ? "source"
            : "elsewhere",
      state: now?.state ?? null,
    });
  }
  return out;
}

const REKEYED_STATES = new Set(["completed", "cleanedUp"]);

/**
 * The card cohort's alert markers the console can see (`cardSettlementAlert` / `migrationAlert`):
 * from the inventory (`reserveShortfallAtSnapshot`, exhausted releases, legacy negative rows, a
 * post-burn settlement the quiesce should have waited for, non-USDC assets left behind), the waiver,
 * and, once the rekey ran, what is still on the source (an open obligation: `settlementRekeyMismatch`
 * / `onMigratedWallet`; a live CCTP settlement or hold).
 */
export function cardCohortMarkers(input: {
  migration: Pick<WalletMigrationDoc, "state"> &
    Partial<Pick<WalletMigrationDoc, "cardKeysWaivedAt">>;
  snapshot: MigrationSnapshot | null;
  sourceObligations: readonly Pick<SettlementObligationDoc, "state">[];
  sourceSettlements: readonly Pick<SettlementDoc, "state">[];
  sourceHolds: readonly Pick<HoldDoc, "state">[];
}): AlertMarker[] {
  const markers: AlertMarker[] = [];
  const inventory = input.snapshot?.cardSettlement ?? null;
  if (inventory && inventory.reserveShortfallMicroUsdc > 0) {
    markers.push({
      kind: "reserveShortfallAtSnapshot",
      label: "Reserve short of its positions",
      color: "red",
      detail:
        "At the snapshot the source Reserve (on chain, plus what an included Reserve leg already moved) was below the open obligations' positions. Paged ERROR, never blocking: reconcile the obligations.",
    });
  }
  if (inventory && inventory.postBurnSettlements.length > 0) {
    markers.push({
      kind: "postBurnAtSnapshot",
      label: "CCTP settlement in flight at the snapshot",
      color: "red",
      detail:
        "The inventory lists a non-terminal CCTP settlement: the quiesce should have waited for it (the list is kept as an assertion).",
    });
  }
  if (inventory && inventory.exhaustedItems.length > 0) {
    markers.push({
      kind: "exhaustedReleases",
      label: `${inventory.exhaustedItems.length} exhausted release(s)`,
      color: "orange",
      detail:
        "Open obligations whose Reserve → Main releases exhausted their attempts: an operator's. They move with the rest.",
    });
  }
  if (inventory && inventory.legacyNegativeObligations.length > 0) {
    markers.push({
      kind: "legacyNegativeObligations",
      label: `${inventory.legacyNegativeObligations.length} legacy negative row(s)`,
      color: "gray",
      detail: "Historical obligations carrying a negative amount: they move as data.",
    });
  }
  if (input.snapshot && input.snapshot.leftBehind.length > 0) {
    markers.push({
      kind: "nonUsdcLeftBehind",
      label: "Non-USDC assets left behind",
      color: "yellow",
      detail:
        "The snapshot found non-USDC assets on the source: the migration does not move them. They stay on the old wallet for the owner's self-exit or support.",
    });
  }
  if (input.migration.cardKeysWaivedAt) {
    markers.push({
      kind: "cardKeysWaived",
      label: "Card keys waived",
      color: "yellow",
      detail:
        "A superAdmin waived the card-cohort gate: the successor has no card keys until the owner approves them, its legs and settlement wait, and card authorizations are declined cardPermissionsRequired an hour after completion.",
    });
  }
  if (REKEYED_STATES.has(input.migration.state)) {
    const open = input.sourceObligations.filter(isOpenObligation);
    if (open.length > 0) {
      markers.push({
        kind: "settlementRekeyMismatch",
        label: `${open.length} open obligation(s) still on the source`,
        color: "red",
        detail:
          "The rekey should have moved every open obligation to the successor (settlementRekeyMismatch at verify; onMigratedWallet when a leg, burn or release finds one). The next leg fire or the completion backstop converges it.",
      });
    }
    const cctp = liveSettlements(input.sourceSettlements);
    if (cctp.length > 0) {
      markers.push({
        kind: "cctpLeftOnSource",
        label: `${cctp.length} CCTP settlement(s) left on the source`,
        color: "orange",
        detail:
          "Post-burn CCTP settlements stay on the old wallet on purpose (their burn's sender is the old Reserve; a failed burn's next attempt mints on the successor). Paged until terminal.",
      });
    }
    const holds = liveHolds(input.sourceHolds);
    if (holds.length > 0) {
      markers.push({
        kind: "holdLeftBehind",
        label: `${holds.length} live hold(s) on the source`,
        color: "orange",
        detail:
          "Card-auth holds move with their obligation and withdrawal announcements are released at completion: a hold still live on the source logs holdLeftBehind (warn).",
      });
    }
  }
  return markers;
}

// ───────────────────────────── approval batch ─────────────────────────────

export const APPROVAL_PURPOSE_LABELS: Readonly<Record<string, string>> = {
  migrateMainTransfer: "Main transfer",
  migrateReserveTransfer: "Reserve transfer",
  forwardMain: "Main forward (90 days)",
  forwardReserve: "Reserve forward (90 days)",
  successorReserveTopUp: "Successor top-up key",
  successorCctpSettlement: "Successor card settlement key",
  successorReserveRelease: "Successor release key",
};

export interface ApprovalBatchRow {
  purpose: string;
  label: string;
  sessionKeyId: string;
  account: string;
  relation: "source" | "successor" | "other";
  type: string;
  /** The key's live state (`sessionKeys`), or null when it cannot be read. */
  state: string | null;
  validUntil: string | null;
  enabledOnChainAt: string | null;
}

type BatchKey = Pick<SessionKeyDoc, "state"> &
  Partial<Pick<SessionKeyDoc, "id" | "validUntil" | "enabledOnChainAt">> & { _docId?: string };

/** The migration's approval batch joined to each key's live state, in batch order. */
export function approvalBatchRows(
  migration: Pick<WalletMigrationDoc, "oldWallet" | "newWallet"> &
    Partial<Pick<WalletMigrationDoc, "approvalSessionKeys">>,
  keys: readonly BatchKey[],
): ApprovalBatchRow[] {
  const byId = new Map(keys.map((k) => [k.id ?? k._docId ?? "", k]));
  return (migration.approvalSessionKeys ?? []).map((entry) => {
    const key = byId.get(entry.sessionKeyId);
    return {
      purpose: entry.purpose,
      label: APPROVAL_PURPOSE_LABELS[entry.purpose] ?? entry.purpose,
      sessionKeyId: entry.sessionKeyId,
      account: entry.account,
      relation:
        entry.account === migration.oldWallet
          ? "source"
          : entry.account === migration.newWallet
            ? "successor"
            : "other",
      type: entry.type,
      state: key?.state ?? null,
      validUntil: key?.validUntil ?? null,
      enabledOnChainAt: key?.enabledOnChainAt ?? null,
    };
  });
}

export function approvalBatchCounts(rows: readonly Pick<ApprovalBatchRow, "state">[]): {
  total: number;
  enabled: number;
  pending: number;
  revoked: number;
  unknown: number;
} {
  const counts = { total: rows.length, enabled: 0, pending: 0, revoked: 0, unknown: 0 };
  for (const row of rows) {
    if (row.state === "enabled") counts.enabled += 1;
    else if (row.state === "pending") counts.pending += 1;
    else if (row.state === "revoked") counts.revoked += 1;
    else counts.unknown += 1;
  }
  return counts;
}

// ───────────────────────────── card-keys waiver ─────────────────────────────

/**
 * Redrive with `waiveCardKeys` (D5): superAdmin in every environment, only in
 * `installingTransferKeys`, once per row. `shown` is false where the button would only be noise.
 */
export function waiveCardKeysAvailability(
  m: Pick<WalletMigrationDoc, "state"> &
    Partial<Pick<WalletMigrationDoc, "cardKeysWaivedAt" | "stallReason">>,
): CommandAvailability & { shown: boolean } {
  if (m.cardKeysWaivedAt)
    return {
      shown: true,
      offered: false,
      note: `Already waived at ${formatTimestamp(m.cardKeysWaivedAt)}: the waiver is once per row.`,
    };
  if (m.state !== "installingTransferKeys")
    return {
      shown: false,
      offered: false,
      note: "Only while the migration waits in installingTransferKeys.",
    };
  const effect =
    "The successor then has no card keys until the owner approves them: its top-up legs and settlement wait, and its card authorizations are declined (cardPermissionsRequired) an hour after completion.";
  return m.stallReason === "cardKeysAwaitingOwner"
    ? {
        shown: true,
        offered: true,
        note: `The transfer keys are enabled and the successor's card keys are not: waived, the row advances now. ${effect}`,
      }
    : {
        shown: true,
        offered: true,
        note: `The card-keys gate is not what holds the row now${m.stallReason ? ` (${m.stallReason})` : ""}: waived, it advances on its transfer keys alone once they are enabled. ${effect}`,
      };
}

// ───────────────────────────── earn drain ─────────────────────────────

export const DRAIN_STATE_LABELS: Readonly<Record<string, string>> = {
  inProgress: "Withdrawing from Earn",
  completed: "Completed",
  abandoned: "Abandoned (the migration failed)",
};

export function drainVaultStatus(
  vault: Pick<WalletMigrationDrainVault, "attempts" | "nextAttemptAt" | "drainedMicroUsdc">,
  drainState: string,
  now: number,
): { label: string; color: MantineColor } {
  const next = ms(vault.nextAttemptAt);
  if (drainState === "inProgress") {
    if (next !== null)
      return next > now
        ? { label: `Retrying (attempt ${vault.attempts} failed)`, color: "orange" }
        : { label: "Retry due", color: "orange" };
    return vault.attempts > 0
      ? { label: "Withdrawing", color: "blue" }
      : { label: "Not started", color: "gray" };
  }
  if (vault.drainedMicroUsdc > 0) return { label: "Drained", color: "green" };
  return {
    label: drainState === "abandoned" ? "Not drained" : "Nothing to withdraw",
    color: "gray",
  };
}

/** The drain's markers: `drainOverdue` (the migration's stall reason) and failed attempts. */
export function drainMarkers(input: {
  drain: Pick<WalletMigrationDrainDoc, "state" | "vaults"> | null;
  migration: Partial<Pick<WalletMigrationDoc, "stallReason">>;
}): AlertMarker[] {
  const markers: AlertMarker[] = [];
  if (input.migration.stallReason === "drainOverdue") {
    markers.push({
      kind: "drainOverdue",
      label: "Drain overdue",
      color: "red",
      detail:
        "The drain is still running past its allowance (1 hour; 5 days for a limUSD source): the stall alert pages. Redrive re-asks earn to drive it at once; an abandon restores the source with its position.",
    });
  }
  const retried = (input.drain?.vaults ?? []).filter((v) => v.attempts > 1);
  if (input.drain?.state === "inProgress" && retried.length > 0) {
    markers.push({
      kind: "drainWithdrawalFailed",
      label: `${retried.length} vault(s) retried`,
      color: "orange",
      detail:
        "Earn retries a failed drain withdrawal at once, then after 5, 10, 20, 40 and 60 minutes, then hourly with no cap; each failure pages drainWithdrawalFailed (WARNING). Open the latest attempt's earn operation for its failure.",
    });
  }
  return markers;
}
